'use client';

import { useEffect, useState } from "react";
import { FaArrowsAltV, FaInfoCircle, FaSyncAlt, FaTimes } from "react-icons/fa";
import { useMouseState } from "../../MouseStateContext";
import styles from "./ScrollTest.module.css";

const SCROLL_TARGET = 300;

export default function ScrollTest() {
    const { mouse, updateMouse } = useMouseState();
    const [allowScrollTest, setAllowScrollTest] = useState(false);
    const [bottom, setBottom] = useState(0);
    const [result, setResult] = useState(
        mouse?.tests?.find((test) => test.name === "ScrollTest")?.result || "initial"
    );

    const setTestResult = (nextResult) => {
        setResult(nextResult);
        updateMouse({
            mouse: {
                ...mouse,
                messageShown: nextResult === "initial" ? false : mouse.messageShown,
                tests: mouse.tests.map((test) =>
                    test.name === "ScrollTest" ? { ...test, result: nextResult } : test
                ),
            },
        });
    };

    const resetTest = () => {
        setBottom(0);
        setTestResult("initial");
    };

    const failTest = () => setTestResult("failed");

    useEffect(() => {
        const intervalId = setInterval(() => {
            setBottom((prev) => {
                if (prev <= 0 || result === "success") return prev;
                return Math.max(0, prev - 5);
            });
        }, 100);
        return () => clearInterval(intervalId);
    }, [result]);

    useEffect(() => {
        if (!allowScrollTest || result === "success" || result === "failed") return;

        if (bottom >= SCROLL_TARGET) {
            setTestResult("success");
            return;
        }

        const handler = (e) => {
            e.preventDefault();
            setBottom((prev) => prev + 3);
        };
        const options = { passive: false };
        window.addEventListener("wheel", handler, options);
        return () => window.removeEventListener("wheel", handler, options);
    }, [bottom, allowScrollTest, result]);

    const scrollProgress = Math.min(100, Math.round((bottom / SCROLL_TARGET) * 100));
    const feedbackClass = result === "success" ? styles.isSuccess : bottom > 0 ? styles.isActive : "";

    return (
        <div
            className={styles.wrapper}
            onMouseEnter={() => setAllowScrollTest(true)}
            onMouseLeave={() => setAllowScrollTest(false)}
        >
            <h2>Scroll Test</h2>
            <div className={styles.relative}>
                {!allowScrollTest && result !== "success" && (
                    <div className={styles.hoverOverlay}>
                        <div>
                            <FaArrowsAltV className={styles.hintIcon} aria-hidden="true" />
                            Hover to activate
                        </div>
                    </div>
                )}
                <div className={styles.body}>
                    <div className={styles.actionsRow}>
                        <span className={`${styles.statusPill} ${styles[`status-${result}`]}`}>
                            {result === "success" ? "Success" : result === "failed" ? "Failed" : "In progress"}
                        </span>
                        <div>
                            <button type="button" className="btn btn-sm btn-warning mx-1" onClick={resetTest}>
                                <FaSyncAlt /> Reset
                            </button>
                            <button type="button" className="btn btn-sm btn-danger mx-1" onClick={failTest}>
                                <FaTimes /> Fail
                            </button>
                        </div>
                    </div>
                    <div className={styles.info}>
                        <FaInfoCircle aria-hidden="true" />
                        <span>
                            Hover over the bar and scroll <b>back and forth</b> with the mouse wheel.
                        </span>
                    </div>
                    <div className={styles.progressBar} aria-hidden="true">
                        <div className={styles.progressFill} style={{ width: `${scrollProgress}%` }} />
                    </div>
                    <div className={`${styles.feedback} ${feedbackClass}`}>
                        {result === "success" && "Scroll target reached — test passed!"}
                        {result === "failed" && "Test marked as failed."}
                        {bottom === 0 && result === "initial" && "Pending — hover over the track"}
                        {bottom > 0 && result === "initial" && `Working… ${scrollProgress}% filled`}
                    </div>
                    <div className={`${styles.shell} ${styles[`result-${result}`]}`}>
                        <div className={styles.trackMarks} aria-hidden="true" />
                        {result !== "success" && result !== "failed" && (
                            <div
                                className={`${styles.runner} ${
                                    bottom < SCROLL_TARGET ? styles.runnerDanger : styles.runnerSuccess
                                }`}
                                style={{ bottom }}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
